/* eslint-disable react/display-name, react/no-multi-comp */
import React from 'react';
import { CaretLeftFilled, CaretRightFilled } from '@ant-design/icons';
import { Button } from '@app/ui';
import classNames from '@app/ui/sports-components/utils/classNames';
import ReactTable from 'react-table';
import * as R from 'ramda';
import './tableLegacy.scss';

type PaginationButtonProps = {
    disabled?: boolean;
    onClick?: () => void;
};

const PreviousPaginationButton = ({
    disabled,
    onClick,
}: PaginationButtonProps) => (
    <Button
        aria-label="Previous page"
        block
        disabled={disabled}
        icon={<CaretLeftFilled />}
        onClick={onClick}
        size="large"
        variant="secondary"
    />
);

const NextPaginationButton = ({ disabled, onClick }: PaginationButtonProps) => (
    <Button
        aria-label="Next page"
        block
        disabled={disabled}
        icon={<CaretRightFilled />}
        onClick={onClick}
        size="large"
        variant="secondary"
    />
);

type TableProps = {
    data: any[];
    columns: any[];
    isStriped?: boolean;
    isHighlight?: boolean;
    isSecondary?: boolean;
    isPrimary?: boolean;
    pageSize?: number;
    style?: any;
    loading?: boolean;
    showPagination?: boolean;
    autoResetHiddenColumns?: boolean;
    resizable?: boolean;
    pageSizeOptions?: number[];
    manual?: boolean;
    onFetchData?: (...args: any[]) => void;
    defaultSorted?: { id: string; desc: boolean }[];
    defaultSortDesc?: boolean;
    minRows?: number;
};

const Table = React.forwardRef<HTMLElement, TableProps>(
    (
        {
            data,
            columns,
            isStriped,
            isHighlight,
            isSecondary,
            isPrimary,
            pageSize = 200,
            pageSizeOptions = [20, 50, 100, 200],
            minRows = 1,
            ...otherProps
        }: TableProps,
        ref,
    ) => {
        const tableClasses = classNames({
            '-striped': isStriped,
            '-highlight': isHighlight,
            '-secondary': isSecondary,
            '-primary': isPrimary,
        });

        const isFilterable =
            R.filter(col => {
                return (
                    R.has('filterMethod', col) ||
                    R.has('Filter', col) ||
                    R.has('filterAll', col) ||
                    (R.has('filterable', col) &&
                        R.prop('filterable', col) !== false)
                );
            }, columns).length > 0;

        return (
            <ReactTable
                ref={ref}
                filterable={isFilterable}
                sortable
                data={data}
                columns={columns}
                className={tableClasses}
                PreviousComponent={PreviousPaginationButton}
                NextComponent={NextPaginationButton}
                previousText={''}
                nextText={''}
                pageSizeOptions={pageSizeOptions}
                defaultPageSize={pageSize}
                minRows={minRows}
                {...otherProps}
            />
        );
    },
);

export default Table;
